import { ALL_US_STATES_TERRITORIES_AND_CANADA_MAP } from '@zg-rentals/states-territories';
import {
  APPLICATIONS_PATH,
  PAYMENTS_PATH,
  PROPERTY_CONDITION_REPORT_PATH,
  RENTAL_MANAGER_PATH,
  RENTAL_PROTECTION_PATH,
} from '../constants/routeConstants';

export default class Property {
  static getLeasesDashboardPath(listingAlias: string): string {
    return `/${RENTAL_MANAGER_PATH}/property/${listingAlias}`;
  }

  static getCombinedOnboardingPath(listingAlias: string) {
    return `${Property.getLeasesDashboardPath(listingAlias)}/create`;
  }

  static getPaymentsDashboardPath(listingAlias: string, encodedLeaseId: string) {
    return `${PAYMENTS_PATH}/${listingAlias}?leaseId=${encodeURIComponent(encodedLeaseId)}`;
  }

  static getPaymentsDashboardPathWithMoveinCostEnabled(listingAlias: string, encodedLeaseId: string) {
    return `${Property.getPaymentsDashboardPath(listingAlias, encodedLeaseId)}&collectMoveInCosts=true`;
  }

  static getApplicationsDashboardPath(listingAlias: string) {
    return `${APPLICATIONS_PATH}?propertyId=${listingAlias}`;
  }

  static getPropertyConditionReportDashboardPath(listingAlias: string) {
    return `${PROPERTY_CONDITION_REPORT_PATH}/${listingAlias}`;
  }

  static getRentalProtectionManagePolicyPath(listingAlias: string, rentalProtectionModuleId: string) {
    return `${RENTAL_PROTECTION_PATH}/${listingAlias}/managePolicies/${rentalProtectionModuleId}`;
  }

  static getPropertyDashboardOverviewPath(listingAlias: string) {
    return `/rental-manager/properties/${listingAlias}/dashboard/overview`;
  }

  id: string;

  listingAlias: string;

  city: string;

  state: string;

  streetAddress: string;

  unit: string;

  zip: string;

  // This is an opinionated property representation expected by the Shared PDP
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdpListing: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(params: any) {
    this.id = params.propertyId || params.id || params.listingAlias;
    this.listingAlias = params.listingAlias;
    this.city = params.city;
    this.state = params.state;
    this.streetAddress = params.streetAddress;
    this.unit = params.unit;
    this.zip = params.zip;
    this.pdpListing = params.pdpListing;
  }

  getFullStreetAddress() {
    return `${this.streetAddress}${this.unit ? ` #${this.unit}` : ''}`;
  }

  getCityStateZip() {
    return `${this.city}, ${this.state} ${this.zip}`;
  }

  // Build relative path for property
  getRelativePath(inputPath: string = null): string {
    let newPath = '';

    if (inputPath != null) {
      newPath = inputPath;
    } else if (typeof window !== 'undefined') {
      newPath = window.location.pathname;
    }

    const regexGroups = newPath.match(/(.*\/property\/[A-Za-z_0-9]+)/);
    if (regexGroups != null) {
      newPath = `${regexGroups[1]}/`;
    } else {
      throw Error(`Could not build a relative path from ${newPath}`);
    }

    return newPath;
  }

  getStateName(): string {
    if (this.state != null) {
      return ALL_US_STATES_TERRITORIES_AND_CANADA_MAP[this.state];
    }

    return null;
  }

  getPdpListing(): object {
    return this.pdpListing;
  }

  getPaymentsDashboardPath(encodedLeaseId: string) {
    return Property.getPaymentsDashboardPath(this.listingAlias, encodedLeaseId);
  }

  getPaymentsDashboardPathWithMoveinCostEnabled(encodedLeaseId: string) {
    return Property.getPaymentsDashboardPathWithMoveinCostEnabled(this.listingAlias, encodedLeaseId);
  }

  getApplicationsDashboardPath() {
    return Property.getApplicationsDashboardPath(this.listingAlias);
  }

  getPropertyConditionReportDashboardPath(listingAlias: string) {
    return Property.getPropertyConditionReportDashboardPath(listingAlias);
  }

  getRentalProtectionManagePolicyPath(listingAlias: string, rentalProtectionModuleId: string) {
    return Property.getRentalProtectionManagePolicyPath(listingAlias, rentalProtectionModuleId);
  }

  getPropertyDashboardOverviewPath() {
    return Property.getPropertyDashboardOverviewPath(this.listingAlias);
  }
}
